import { GlobalVariables } from 'app/services/globals';
import {
    ConnectionKind,
    IAttribute,
    Metamodel,
    ModelConstants,
    shellStringResources,
    stringIsNullOrEmpty
} from 'shell-models-lib';
import { isString } from 'lodash';

export const isWorkspaceBuiltInType = (linkedServiceType: string): boolean => {
    return (
        linkedServiceType === ModelConstants.kustoDatabaseLinkedService ||
        linkedServiceType === ModelConstants.dataWarehouseLinkedService ||
        linkedServiceType === ModelConstants.lakehouseLinkedService ||
        linkedServiceType === ModelConstants.fabricSqlLinkedService ||
        linkedServiceType === ModelConstants.mountedRelationalDatabaseLinkedService ||
        linkedServiceType === ModelConstants.sqlAnalyticsEndpointLinkedService ||
        linkedServiceType === ModelConstants.eventStreamLinkedService
    );
};

export const workspaceBuiltInTypeDisplayName = (linkedServiceType: string): string => {
    switch (linkedServiceType) {
        case ModelConstants.kustoDatabaseLinkedService:
            return stringResources.KustoDatabase_DisplayText;
        case ModelConstants.lakehouseLinkedService:
            return stringResources.Lakehouse_DisplayText;
        case ModelConstants.dataWarehouseLinkedService:
            return shellStringResources.Warehouse_DisplayText;
        case ModelConstants.fabricSqlLinkedService:
            return shellStringResources.SqlDatabase_DisplayText;
        case ModelConstants.mountedRelationalDatabaseLinkedService:
            return shellStringResources.MountedRelationalDatabase_DisplayText;
        case ModelConstants.sqlAnalyticsEndpointLinkedService:
            return shellStringResources.SqlAnalyticsEndpoint_DisplayText;
        case ModelConstants.eventStreamLinkedService:
            return stringResources.EventStream_DisplayText;
        default:
            return undefined;
    }
};

/**
 * In Trident, Azure Data Explorer, Cosmos DB for NoSQL, and Snowflake connection don't have database information
 * This will make database field in dataset required, and we need database information before to list tables.
 *
 * @param connectionKind
 * @return true if connection needs database information and database is mandatory
 */
export const connectionNeedDatabase = (connectionKind: ConnectionKind): boolean =>
    ConnectionKind.AZURE_COSMOS_DB_NOSQL_V2 === connectionKind ||
    ConnectionKind.AZURE_DATA_EXPLORER === connectionKind ||
    ConnectionKind.SNOWFLAKE === connectionKind ||
    ConnectionKind.AZURE_COSMOSDB_FOR_MONGODB === connectionKind ||
    !GlobalVariables.enableSapCdcCopy && ConnectionKind.SAPCDC === connectionKind ||
    [ConnectionKind.MONGODB, ConnectionKind.MONGODB_ATLAS].includes(connectionKind);

/**
 * This means we can list databases for this connection
 * @param connectionKind
 */
export const connectionSupportEnumerateDatabase = (connectionKind: ConnectionKind): boolean =>
    ConnectionKind.AZURE_COSMOS_DB_NOSQL_V2 === connectionKind ||
    ConnectionKind.AZURE_DATA_EXPLORER === connectionKind ||
    ConnectionKind.SNOWFLAKE === connectionKind ||
    ConnectionKind.SQL === connectionKind ||
    !GlobalVariables.enableSapCdcCopy && ConnectionKind.SAPCDC === connectionKind ||
    [ConnectionKind.MONGODB, ConnectionKind.MONGODB_ATLAS].includes(connectionKind) ||
    GlobalVariables.enableAzDatabricksCatalog && ConnectionKind.DATABRICKS === connectionKind ||
    GlobalVariables.enableDatabricks && ConnectionKind.DATABRICKS_MULTI_CLOUD === connectionKind ||
    ConnectionKind.AZURE_COSMOSDB_FOR_MONGODB === connectionKind;

export const propertyIsNullOrEmpty = (entity: unknown, attribute: IAttribute): boolean => {
    return stringIsNullOrEmpty(attribute.value(entity)) && stringIsNullOrEmpty(attribute.templateValue(entity));
};

export const attributeIsParameterized = (entity: unknown, attribute: string | IAttribute): boolean => {
    if (isString(attribute)) {
        const model = Metamodel.modelOf(entity);
        if (model == null) {
            return false;
        }
        if (!model.hasAttribute(attribute)) {
            return false;
        }
        return !stringIsNullOrEmpty(model.attribute(attribute)?.templateValue(entity));
    } else {
        return !stringIsNullOrEmpty(attribute.templateValue(entity));
    }
};
